/**
 * useConversationMedia - Galeria de mídia paginada da conversa
 *
 * Substitui o antigo `conversation.files` (que vinha agregado inteiro no
 * documento da conversa e estourava o limite de 16MB do MongoDB). A mídia agora
 * é servida paginada por `GET /conversations/:id/media`, filtrada por tipo.
 *
 * As contagens por tipo (`counts`) são globais — vêm iguais em toda página,
 * independente do filtro de `type` — então os badges das sub-abas ficam sempre
 * corretos mesmo navegando entre tipos.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ConversationMediaType } from '@troia-v3/types';
import { chatService } from '../services';

const DEFAULT_LIMIT = 30;
const SUMMARY_LIMIT = 5;

export const useConversationMedia = (
  conversationId: string | undefined,
  // `undefined` = todos os tipos. A rota já trata assim (`if (params?.type)` em
  // `chatService.getConversationMedia`); declarar o parâmetro como obrigatório
  // obrigava a aba "Tudo" a mentir para o compilador com uma conversão forçada.
  type: ConversationMediaType | undefined,
  enabled = true,
) => {
  return useInfiniteQuery({
    queryKey: ['conversation-media', conversationId, type],
    queryFn: ({ pageParam, signal }) =>
      chatService.getConversationMedia(
        conversationId!,
        { type, page: pageParam as number, limit: DEFAULT_LIMIT },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.limit;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled: enabled && !!conversationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Resumo leve da mídia para a sidebar: contagens por tipo + as 5 mídias mais
 * recentes (todos os tipos). Pensado para ser chamado de forma preguiçosa —
 * passe `enabled=false` enquanto a seção estiver recolhida para evitar N+1.
 */
export const useConversationMediaSummary = (
  conversationId: string | undefined,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['conversation-media-summary', conversationId],
    queryFn: ({ signal }) =>
      chatService.getConversationMedia(conversationId!, { limit: SUMMARY_LIMIT }, signal),
    enabled: enabled && !!conversationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};
